import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CircleCheck, AlertCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { SITE, SERVICE_TYPES, CITIES } from "../lib/site";
import { useLocation as useGeo } from "../contexts/LocationContext";
import SectionHeading from "../components/SectionHeading";
import useDocumentTitle from "../hooks/useDocumentTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQ = [
  { q: "Quels sont vos délais d'intervention ?", a: "En zone urbaine, nous arrivons en 30 minutes en moyenne pour les urgences. Pour les installations sur devis, l'intervention est planifiée selon votre disponibilité." },
  { q: "Le devis est-il gratuit ?", a: "Oui, le devis est entièrement gratuit et sans engagement. Nous vous le remettons sous 24h à 48h après visite ou échange téléphonique." },
  { q: "Intervenez-vous le week-end et la nuit ?", a: "Oui, pour les urgences électriques (panne, court-circuit, disjoncteur), nous sommes disponibles 24h/24, 7j/7, week-ends et jours fériés inclus." },
  { q: "Avez-vous une assurance professionnelle ?", a: "Oui, nous disposons d'une responsabilité civile professionnelle et d'une garantie décennale couvrant tous nos travaux." },
  { q: "Travaillez-vous pour les professionnels ?", a: "Oui, nous intervenons pour les particuliers, les commerces, les bureaux et les copropriétés. N'hésitez pas à nous demander un devis adapté." },
];

export default function Contact() {
  const { city } = useGeo();
  useDocumentTitle(
    city
      ? `Contact & Devis gratuit à ${city} — Voltis Électricité`
      : "Contact & Devis gratuit — Voltis Électricité",
    city
      ? `Contactez Voltis Électricité à ${city} : devis gratuit sous 24h, intervention rapide 24/7.`
      : "Contactez Voltis Électricité : devis gratuit sous 24h, intervention rapide 24/7."
  );
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    service_type: "", city: city || "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      toast.success("Demande envoyée. Nous vous recontactons rapidement.");
      setForm({ name: "", email: "", phone: "", service_type: "", city: "", message: "" });
    } catch (err) {
      toast.error("Erreur lors de l'envoi. Vous pouvez nous appeler directement.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-slate-50">
        <div className="container-x py-14 lg:py-20">
          <div className="max-w-3xl">
            <span className="accent-bar mb-5" />
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-slate-900 mb-3">
              Contact
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
              {city ? (
                <>
                  Contactez votre électricien à{" "}
                  <span className="text-[#EAB308]">{city}.</span>
                </>
              ) : (
                <>
                  Parlons{" "}
                  <span className="text-[#EAB308]">de votre projet.</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              {city ? (
                <>Une urgence à <strong>{city}</strong>, un devis, une question ? Remplissez le
                formulaire ou appelez-nous directement.</>
              ) : (
                <>Une urgence, un devis, une question ? Remplissez le formulaire
                ou appelez-nous directement.</>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="section-padding bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            {success ? (
              <div data-testid="contact-success" className="bg-[#0F172A] text-white p-10 lg:p-14">
                <CircleCheck className="w-12 h-12 text-[#EAB308] mb-5" strokeWidth={1.5} />
                <h2 className="font-heading text-3xl font-bold mb-3">Demande bien reçue.</h2>
                <p className="text-slate-300 mb-6">
                  Merci pour votre message. Nous revenons vers vous dans les plus brefs délais.
                  Pour une urgence, n'hésitez pas à nous appeler.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a href={`tel:${SITE.phoneTel}`} className="btn-primary justify-center">
                    <Phone className="w-5 h-5" /> {SITE.phoneDisplay}
                  </a>
                  <button
                    onClick={() => setSuccess(false)}
                    data-testid="contact-new-btn"
                    className="bg-white text-slate-900 hover:bg-slate-100 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-semibold rounded-sm transition-colors"
                  >
                    Envoyer une autre demande
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5">
                <SectionHeading
                  eyebrow="Formulaire"
                  title="Demander un devis ou un dépannage."
                />
                <div className="grid sm:grid-cols-2 gap-5 mt-10">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-slate-900">Nom complet *</Label>
                    <Input
                      id="name" data-testid="form-name"
                      value={form.name} onChange={update("name")}
                      required minLength={2}
                      placeholder="Jean Dupont"
                      className="mt-2 h-12 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#EAB308] focus-visible:ring-offset-0 focus-visible:border-[#EAB308]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-900">Téléphone *</Label>
                    <Input
                      id="phone" data-testid="form-phone" type="tel"
                      value={form.phone} onChange={update("phone")}
                      required
                      placeholder="06 12 34 56 78"
                      className="mt-2 h-12 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#EAB308] focus-visible:ring-offset-0 focus-visible:border-[#EAB308]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-slate-900">Email *</Label>
                  <Input
                    id="email" data-testid="form-email" type="email"
                    value={form.email} onChange={update("email")}
                    required
                    placeholder="vous@email.fr"
                    className="mt-2 h-12 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#EAB308] focus-visible:ring-offset-0 focus-visible:border-[#EAB308]"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-medium text-slate-900">Type de service</Label>
                    <Select value={form.service_type} onValueChange={update("service_type")}>
                      <SelectTrigger data-testid="form-service" className="mt-2 h-12 rounded-sm border-slate-300 focus:ring-2 focus:ring-[#EAB308] focus:ring-offset-0">
                        <SelectValue placeholder="Choisir un service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_TYPES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-900">Ville</Label>
                    <Select value={form.city} onValueChange={update("city")}>
                      <SelectTrigger data-testid="form-city" className="mt-2 h-12 rounded-sm border-slate-300 focus:ring-2 focus:ring-[#EAB308] focus:ring-offset-0">
                        <SelectValue placeholder="Sélectionner votre ville" />
                      </SelectTrigger>
                      <SelectContent>
                        {CITIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                        <SelectItem value="Autre">Autre ville</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-medium text-slate-900">Décrivez votre demande *</Label>
                  <Textarea
                    id="message" data-testid="form-message"
                    value={form.message} onChange={update("message")}
                    required minLength={5} rows={5}
                    placeholder="Précisez la nature du besoin, les contraintes, l'urgence..."
                    className="mt-2 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#EAB308] focus-visible:ring-offset-0 focus-visible:border-[#EAB308]"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="btn-primary text-base disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
                  </button>
                  <a href={`tel:${SITE.phoneTel}`} className="btn-outline text-base">
                    <Phone className="w-5 h-5" /> Appeler directement
                  </a>
                </div>

                <p className="text-xs text-slate-500 flex items-start gap-2 pt-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Vos informations restent confidentielles et servent uniquement à vous recontacter.
                </p>
              </form>
            )}
          </div>

          {/* Info side */}
          <aside className="lg:col-span-5">
            <div className="sticky top-28 space-y-5">
              <div className="bg-[#0F172A] text-white p-8">
                <h3 className="font-heading text-xl font-bold mb-6">Coordonnées</h3>
                <ul className="space-y-5">
                  <li className="flex items-start gap-4">
                    <span className="inline-flex w-10 h-10 shrink-0 items-center justify-center bg-[#EAB308] text-slate-900">
                      <Phone className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400">Téléphone</div>
                      <a href={`tel:${SITE.phoneTel}`} data-testid="info-phone" className="font-semibold text-white hover:text-[#EAB308] transition-colors">{SITE.phoneDisplay}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="inline-flex w-10 h-10 shrink-0 items-center justify-center bg-[#EAB308] text-slate-900">
                      <Mail className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400">Email</div>
                      <a href={`mailto:${SITE.email}`} data-testid="info-email" className="font-semibold text-white hover:text-[#EAB308] transition-colors break-all">{SITE.email}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="inline-flex w-10 h-10 shrink-0 items-center justify-center bg-[#EAB308] text-slate-900">
                      <MapPin className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400">Zone</div>
                      <p className="font-semibold text-white">Votre ville et alentours</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="inline-flex w-10 h-10 shrink-0 items-center justify-center bg-[#EAB308] text-slate-900">
                      <Clock className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400">Horaires</div>
                      <p className="font-semibold text-white">{SITE.hours}</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="border border-slate-200 p-7">
                <p className="uppercase tracking-[0.2em] text-xs font-semibold text-[#EAB308] mb-2">Bon à savoir</p>
                <h4 className="font-heading text-lg font-bold text-slate-900 mb-3">Devis gratuit sous 24h</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Estimation transparente, sans engagement. Tarif clair annoncé avant toute intervention.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-slate-50">
        <div className="container-x max-w-4xl">
          <SectionHeading
            eyebrow="Questions fréquentes"
            title="Les réponses à vos questions."
            align="center"
          />
          <Accordion type="single" collapsible className="mt-12 space-y-3">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`} data-testid={`faq-${i}`} className="border border-slate-200 bg-white px-6 rounded-sm">
                <AccordionTrigger className="text-left font-heading font-semibold text-slate-900 hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
